import type { EChartsCoreOption } from 'echarts/core';
import { useEffect, useMemo, useState } from 'react';
import { Grafico } from '../../design/graficos/Grafico';
import { lerDaApi } from '../../giro';
import { lerMeuBonus, valorDoIndicador } from '../../metas';
import { useValoresOcultos } from '../../design/sigilo';
import type { IndicadorDoCatalogo } from '../../metas';
import './meu-bonus.css';

/**
 * A ANÁLISE DO MEU DESEMPENHO (dono, 29/09 — fase 4): "como estou performando". Gráficos com a
 * evolução, a média de 12 meses e — quando há meta que paga — os níveis dela; na margem, as
 * faixas de saúde (cor ≠ nível da meta). Só médias e razões (regra do dono de 27/09). Os números
 * vêm do servidor; aqui só o % de variação entre dois números dele, para mostrar.
 */

interface IndicadorDaAnalise {
  readonly codigo: string;
  readonly rotulo: string;
  readonly unidade: IndicadorDoCatalogo['unidade'];
  readonly casas: number;
  readonly direcao: 'MAIOR_MELHOR' | 'MENOR_MELHOR';
  readonly valor: string | null;
  readonly anterior: string | null;
  readonly media3: string | null;
  readonly media6: string | null;
  readonly media12: string | null;
  readonly serie: readonly { rotulo: string; valor: string | null }[];
}
interface MinhaEvolucao {
  readonly de: string;
  readonly ate: string;
  readonly granularidade: 'dia' | 'mes';
  readonly faixasDaMargem: { amarelo: number; azul: number; verde: number };
  readonly indicadores: readonly IndicadorDaAnalise[];
}

type Filtro =
  'hoje' | 'ontem' | '7d' | '30d' | 'mes' | 'mesAnterior' | '3m' | '6m' | '12m' | 'livre';
const FILTROS: readonly [Filtro, string][] = [
  ['hoje', 'Hoje'],
  ['ontem', 'Ontem'],
  ['7d', '7 dias'],
  ['30d', '30 dias'],
  ['mes', 'Mês atual'],
  ['mesAnterior', 'Mês anterior'],
  ['3m', '3 meses'],
  ['6m', '6 meses'],
  ['12m', '12 meses'],
  ['livre', 'Personalizado'],
];
const hoje = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
const menos = (d: string, n: number) =>
  new Date(Date.parse(`${d}T12:00:00Z`) - n * 86_400_000).toISOString().slice(0, 10);
function periodo(f: Filtro, livre: { de: string; ate: string }): { de: string; ate: string } {
  const h = hoje();
  const [a, m] = h.split('-').map(Number);
  const primeiro = (k: number) =>
    new Date(Date.UTC(a ?? 2026, (m ?? 1) - 1 - k, 1)).toISOString().slice(0, 10);
  switch (f) {
    case 'hoje':
      return { de: h, ate: h };
    case 'ontem':
      return { de: menos(h, 1), ate: menos(h, 1) };
    case '7d':
      return { de: menos(h, 6), ate: h };
    case '30d':
      return { de: menos(h, 29), ate: h };
    case 'mes':
      return { de: primeiro(0), ate: h };
    case 'mesAnterior':
      return { de: primeiro(1), ate: menos(primeiro(0), 1) };
    case '3m':
      return { de: primeiro(2), ate: h };
    case '6m':
      return { de: primeiro(5), ate: h };
    case '12m':
      return { de: primeiro(11), ate: h };
    case 'livre':
      return livre;
  }
}
const rotuloDoBalde = (r: string, g: 'dia' | 'mes') =>
  g === 'dia' ? `${r.slice(8, 10)}/${r.slice(5, 7)}` : `${r.slice(5, 7)}/${r.slice(2, 4)}`;

/** Variação % para mostrar (+7,4%) — e se é boa, conforme a direção do indicador. */
function variacao(
  atual: string | null,
  ref: string | null,
  direcao: IndicadorDaAnalise['direcao'],
) {
  if (atual === null || ref === null || Number(ref) === 0) return null;
  const pct = ((Number(atual) - Number(ref)) / Math.abs(Number(ref))) * 100;
  const boa = direcao === 'MAIOR_MELHOR' ? pct >= 0 : pct <= 0;
  const txt = `${pct >= 0 ? '+' : ''}${pct.toLocaleString('pt-BR', { maximumFractionDigits: 1, minimumFractionDigits: 1 })}%`;
  return { txt, boa };
}

export function AnaliseDetalhada() {
  const ocultos = useValoresOcultos();
  const [filtro, setFiltro] = useState<Filtro>('mes');
  const [livre, setLivre] = useState({ de: menos(hoje(), 29), ate: hoje() });
  const [d, setD] = useState<MinhaEvolucao | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [alvos, setAlvos] = useState<Map<string, readonly string[]>>(new Map());
  const p = periodo(filtro, livre);

  useEffect(() => {
    // Os níveis das metas que PAGAM, para riscar nos gráficos (o mesmo dado do topo).
    void lerMeuBonus().then((r) => {
      if (!r.ok) return;
      const m = new Map<string, readonly string[]>();
      for (const x of r.dados.mes?.metas ?? [])
        if (x.comPremio && x.avaliacao?.alvos && !m.has(x.indicador))
          m.set(x.indicador, x.avaliacao.alvos);
      setAlvos(m);
    });
  }, []);
  useEffect(() => {
    let vivo = true;
    setFalhou(false);
    setD(null);
    void lerDaApi<MinhaEvolucao>(`/api/balconista/minha-evolucao?de=${p.de}&ate=${p.ate}`).then(
      (r) => {
        if (!vivo) return;
        if (r.ok) setD(r.dados);
        else setFalhou(true);
      },
    );
    return () => {
      vivo = false;
    };
  }, [p.de, p.ate]);

  // indicador em R$ (ticket, vendido…) é quanto ele vendeu: no "só números" sai o cartão e o gráfico
  const indicadores = d?.indicadores.filter((i) => !(ocultos && i.unidade === 'RS')) ?? [];
  return (
    <section className="an" aria-label="Minha evolução">
      <div className="an-filtros" role="tablist" aria-label="Período da análise">
        {FILTROS.map(([f, rotulo]) => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={filtro === f}
            className={`an-filtro${filtro === f ? ' an-filtro-sel' : ''}`}
            onClick={() => setFiltro(f)}
          >
            {rotulo}
          </button>
        ))}
      </div>
      {filtro === 'livre' && (
        <div className="an-livre">
          <label>
            De{' '}
            <input
              type="date"
              value={livre.de}
              max={livre.ate}
              onChange={(e) => setLivre((l) => ({ ...l, de: e.target.value }))}
            />
          </label>
          <label>
            até{' '}
            <input
              type="date"
              value={livre.ate}
              min={livre.de}
              max={hoje()}
              onChange={(e) => setLivre((l) => ({ ...l, ate: e.target.value }))}
            />
          </label>
        </div>
      )}
      <p className="an-nota">
        As metas do mês continuam sendo do mês — este período vale só para a análise.
      </p>
      {falhou && <p className="mb-meta-msg">Não foi possível carregar a sua evolução agora.</p>}
      {!falhou && !d && (
        <div className="mb-esqueleto" aria-busy="true">
          <i />
        </div>
      )}
      {d && (
        <>
          <div className="an-cartoes">
            {indicadores.map((i) => (
              <Cartao key={i.codigo} i={i} />
            ))}
          </div>
          <div className="an-graficos">
            {indicadores
              .filter((i) => i.serie.some((x) => x.valor !== null))
              .map((i) => (
                <GraficoDoIndicador
                  key={i.codigo}
                  i={i}
                  d={d}
                  alvos={alvos.get(i.codigo) ?? null}
                />
              ))}
          </div>
          {indicadores.every((i) => i.serie.every((x) => x.valor === null)) && (
            <p className="mb-meta-msg">
              Sem vendas suficientes neste período para desenhar os gráficos.
            </p>
          )}
        </>
      )}
    </section>
  );
}

function Cartao({ i }: { i: IndicadorDaAnalise }) {
  const comps: [string, string | null][] = [
    ['vs período anterior', i.anterior],
    ['vs média 3 meses', i.media3],
    ['vs média 6 meses', i.media6],
    ['vs média 12 meses', i.media12],
  ];
  return (
    <div className="an-cartao">
      <span className="an-rotulo">{i.rotulo}</span>
      <strong>{i.valor === null ? '—' : valorDoIndicador(i.valor, i.unidade, i.casas)}</strong>
      {i.valor === null ? (
        <em className="an-vazio">Sem vendas suficientes para calcular.</em>
      ) : (
        <ul>
          {comps.map(([rot, ref]) => {
            const v = variacao(i.valor, ref, i.direcao);
            return v ? (
              <li key={rot} className={v.boa ? 'an-bom' : 'an-ruim'}>
                <b>{v.txt}</b> {rot}
              </li>
            ) : null;
          })}
        </ul>
      )}
    </div>
  );
}

function GraficoDoIndicador({
  i,
  d,
  alvos,
}: {
  i: IndicadorDaAnalise;
  d: MinhaEvolucao;
  alvos: readonly string[] | null;
}) {
  const opcao = useMemo<EChartsCoreOption>(() => {
    const fmt = (v: number) => valorDoIndicador(String(v), i.unidade, i.casas);
    const linhas: object[] = [];
    if (i.media12 !== null)
      linhas.push({
        yAxis: Number(i.media12),
        name: 'média 12m',
        lineStyle: { type: 'dashed', color: '#8e959b' },
        label: { formatter: 'média 12m', color: '#6b7075', fontSize: 10 },
      });
    if (alvos)
      alvos.forEach((a, k) =>
        linhas.push({
          yAxis: Number(a),
          name: `N${String(k + 1)}`,
          lineStyle: { type: 'dotted', color: '#1a7f55' },
          label: { formatter: `N${String(k + 1)}`, color: '#1a7f55', fontSize: 10 },
        }),
      );
    const f = d.faixasDaMargem;
    const faixas =
      i.codigo === 'margem_pct'
        ? {
            silent: true,
            data: [
              [{ yAxis: 0, itemStyle: { color: 'rgba(203,27,43,0.06)' } }, { yAxis: f.amarelo }],
              [
                { yAxis: f.amarelo, itemStyle: { color: 'rgba(183,121,31,0.08)' } },
                { yAxis: f.azul },
              ],
              [{ yAxis: f.azul, itemStyle: { color: 'rgba(26,92,158,0.07)' } }, { yAxis: f.verde }],
              [{ yAxis: f.verde, itemStyle: { color: 'rgba(26,127,85,0.08)' } }, { yAxis: 100 }],
            ],
          }
        : undefined;
    const valores = i.serie.map((x) => (x.valor === null ? null : Number(x.valor)));
    const numeros = [
      ...valores.filter((x): x is number => x !== null),
      ...(alvos ?? []).map(Number),
      ...(i.media12 ? [Number(i.media12)] : []),
    ];
    // Eixo com números redondos que ainda mostram as linhas dos níveis e a média.
    const min = Math.min(...numeros);
    const max = Math.max(...numeros);
    const passo = 10 ** Math.floor(Math.log10(Math.max(1e-9, max - min || Math.abs(max) || 1)));
    const piso = Math.floor(min / passo) * passo;
    return {
      grid: { left: 62, right: 72, top: 16, bottom: 28 },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v: unknown) => (typeof v === 'number' ? fmt(v) : 'sem venda'),
      },
      xAxis: {
        type: 'category',
        data: i.serie.map((x) => rotuloDoBalde(x.rotulo, d.granularidade)),
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        scale: true,
        min: (i.unidade === 'PCT' || i.unidade === 'RS') && piso < 0 ? 0 : piso,
        max: Math.ceil(max / passo) * passo,
        axisLabel: { formatter: (v: number) => fmt(v) },
        splitLine: { lineStyle: { color: '#eef0f2' } },
      },
      series: [
        {
          name: i.rotulo,
          type: 'line',
          smooth: true,
          connectNulls: false,
          showSymbol: d.granularidade === 'mes' || i.serie.length <= 14,
          data: valores,
          lineStyle: { width: 2.5 },
          markLine: linhas.length ? { symbol: 'none', silent: true, data: linhas } : undefined,
          markArea: faixas,
        },
      ],
    };
  }, [i, d, alvos]);
  return (
    <div className="an-grafico">
      <h3>{i.rotulo}</h3>
      <Grafico opcao={opcao} altura={210} rotulo={`${i.rotulo} no período`} />
      {i.codigo === 'margem_pct' && (
        <p className="an-nota">
          Cores ao fundo = saúde da margem (vermelho, amarelo, azul, verde). Isso não é o nível da
          meta.
        </p>
      )}
    </div>
  );
}
